import { useEffect, useState, type CSSProperties } from 'react'
import { useNavigate } from 'react-router-dom'
import { Spinner, SpinnerSize, MessageBar, MessageBarType, Icon } from '@fluentui/react'
import { apiService } from '../../services/apiService'
import { useDashboardTokens, type DashboardTokens } from '../../theme/dashboardTokens'
import type { RecentChangeFeedRow } from '../../types'

type Category = 'added' | 'changed' | 'cred' | 'removed'

type FeedRow = RecentChangeFeedRow

interface RecentChangesFeedProps {
  heading?: string
}

export function RecentChangesFeed({ heading = 'Recent changes' }: RecentChangesFeedProps) {
  const navigate = useNavigate()
  const tokens = useDashboardTokens()

  const [rows, setRows] = useState<FeedRow[] | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    apiService.getRecentChanges(14, 30).then((data) => {
      if (!cancelled) setRows(data.rows)
    }).catch((e) => {
      if (!cancelled) setError(e instanceof Error ? e.message : String(e))
    }).finally(() => {
      if (!cancelled) setLoading(false)
    })
    return () => { cancelled = true }
  }, [])

  // Group rows by day, preserving order (already most-recent-first).
  const groups: Array<{ date: string; rows: FeedRow[] }> = []
  for (const row of rows ?? []) {
    const last = groups[groups.length - 1]
    if (last && last.date === row.date) last.rows.push(row)
    else groups.push({ date: row.date, rows: [row] })
  }

  return (
    <section aria-label="Recent changes" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
        <h2 style={{ fontSize: 15, fontWeight: 600, color: tokens.text, margin: 0 }}>{heading}</h2>
        <button
          type="button"
          onClick={() => navigate('/reports')}
          aria-label="View all changes on the Reports page"
          style={{
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            color: tokens.changed,
            fontSize: 13,
            fontWeight: 600,
            padding: '2px 4px',
          }}
        >
          View all →
        </button>
      </div>

      {loading ? (
        <div style={{ padding: '16px 4px' }}>
          <Spinner size={SpinnerSize.small} label="Loading recent changes…" labelPosition="right" ariaLive="polite" />
        </div>
      ) : error ? (
        <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar>
      ) : !groups.length ? (
        <div style={{ padding: '16px 4px', color: tokens.muted, fontSize: 13 }}>
          No changes recorded in the last 30 days.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {groups.map((g) => (
            <div key={g.date} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: 0.4,
                  textTransform: 'uppercase',
                  color: tokens.muted,
                  padding: '0 4px',
                }}
              >
                {relativeDay(g.date)}
              </div>
              {g.rows.map((row, i) => (
                <FeedRowButton
                  key={`${row.date}-${row.category}-${i}`}
                  row={row}
                  tokens={tokens}
                  onClick={() => navigate(`/changes/${row.date}`)}
                />
              ))}
            </div>
          ))}
        </div>
      )}
    </section>
  )
}

// ─────────────────────────────────────────────────────────────────────────────

function categoryMeta(tokens: DashboardTokens) {
  return {
    added: { label: 'Added', color: tokens.added, icon: 'CircleAdditionSolid' },
    changed: { label: 'Changed', color: tokens.changed, icon: 'Edit' },
    cred: { label: 'Credential', color: tokens.cred, icon: 'Certificate' },
    removed: { label: 'Removed', color: tokens.removed, icon: 'Blocked2Solid' },
  } as const
}

function FeedRowButton({
  row,
  tokens,
  onClick,
}: {
  row: FeedRow
  tokens: DashboardTokens
  onClick: () => void
}) {
  const meta = categoryMeta(tokens)[row.category]
  const rowStyle: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    width: '100%',
    textAlign: 'left',
    background: 'transparent',
    border: 'none',
    borderRadius: 8,
    padding: '7px 8px',
    cursor: 'pointer',
  }
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={`${meta.label}: ${row.title}${row.subtitle ? `. ${row.subtitle}` : ''}. ${relativeDay(row.date)}. View day detail.`}
      style={rowStyle}
    >
      {/* Source glyph — colour + icon (not colour-alone) */}
      <span
        aria-hidden
        style={{
          flex: '0 0 auto',
          width: 24,
          height: 24,
          borderRadius: 6,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: softBg(row.category, tokens),
          color: meta.color,
        }}
      >
        <Icon iconName={meta.icon} style={{ fontSize: 13 }} />
      </span>
      <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
        <span
          style={{
            fontSize: 13,
            fontWeight: 600,
            color: tokens.text,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {row.title}
        </span>
        <span style={{ fontSize: 12, color: tokens.slate }}>
          {meta.label}
          {row.subtitle ? ` · ${row.subtitle}` : ''}
        </span>
      </span>
      <Icon iconName="ChevronRight" style={{ fontSize: 11, color: tokens.muted, flex: '0 0 auto' }} aria-hidden />
    </button>
  )
}

function softBg(category: Category, tokens: DashboardTokens): string {
  switch (category) {
    case 'added':
      return tokens.addedSoft
    case 'changed':
      return tokens.changedSoft
    case 'cred':
      return tokens.credSoft
    case 'removed':
      return tokens.removedSoft
  }
}

/** Relative day label vs the local "today". */
function relativeDay(iso: string): string {
  const d = new Date(`${iso}T00:00:00`)
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const diffDays = Math.round((today.getTime() - d.getTime()) / 86400000)
  if (diffDays <= 0) return 'Today'
  if (diffDays === 1) return 'Yesterday'
  if (diffDays < 7) return `${diffDays} days ago`
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
}

export default RecentChangesFeed
